import { useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Hook for consistent content ownership checks across comments and replies.
 *
 * Addresses the pattern inconsistency between CommentBox (useCallback with auth fallback)
 * and ReplyItem (inline check without fallback).
 *
 * Strategy:
 * 1. Prefer UID match for reliable ownership verification
 * 2. Fallback to username comparison ONLY for legacy data without userId
 * 3. Normalize usernames to lowercase for case-insensitive comparison
 */
export const useContentOwnership = () => {
  const { currentUser, userData } = useAuth();

  const uid = currentUser?.uid || null;
  const username = (
    userData?.username ||
    userData?.displayName ||
    currentUser?.displayName ||
    ""
  )
    .trim()
    .toLowerCase();

  /**
   * Check if current user can manage (edit/delete) a content item
   * @param {Object} item - Comment or reply object with userId and/or username
   * @returns {boolean} - True if user owns this content
   */
  const canManage = useCallback(
    (item) => {
      if (!uid || !item) return false;

      // Primary: UID match (most reliable)
      if (item.userId && item.userId === uid) {
        return true;
      }

      // Fallback: Username match for legacy data (only if no userId present)
      if (!item.userId) {
        const itemUsername = (item.username || "").trim().toLowerCase();
        return username && itemUsername && username === itemUsername;
      }

      return false;
    },
    [uid, username, userData?.username, userData?.displayName]
  );

  /**
   * Check if an item is owned by a specific user (for display purposes)
   * @param {Object} item - Comment or reply object
   * @param {string} targetUid - User ID to check against
   * @returns {boolean}
   */
  const isOwnedBy = useCallback(
    (item, targetUid) => {
      if (!item || !targetUid) return false;
      return item.userId === targetUid;
    },
    []
  );

  return {
    canManage,
    isOwnedBy,
    uid,
    username,
  };
};

export default useContentOwnership;
